// hooks/useSalonHome.ts
import { useState, useEffect } from "react";
import { CustomerDetails, StylingStaff } from "../types/salon.types";
import { useSalonData } from "./useSaloonData";
import { useOrderManagement } from "./useOrderManagement";
import { usePaymentProcessing } from "./usePaymentProcessing";
import { useCategoryFilter } from "./useCategoryFilter";
import { useOrderFlow } from "./useOrderFlow";
import { completeOrder, fetchOutstandingBalance } from "./OrderService";
import { addCustomer } from "./customerService";
import { notifyError, notifySuccess, notifyWarning } from "src/assets/toastService";
import { PrintData } from "src/printConfigration/PrintConfigration";
import { roundAmount } from "../utils/formatters";

export const useSalonHome = () => {
  const [activeTab, setActiveTab] = useState<"Staffs" | "Services">("Staffs");
  const [selectedStaff, setSelectedStaff] = useState<StylingStaff | null>(null);
  const [isCustomerDialogOpen, setCustomerDialogOpen] = useState(false);
  const [resetCategoryFlag, setResetCategoryFlag] = useState(false);

  const [outstandingBalance, setOutstandingBalance] = useState(0);
  const { staffMembers, products, categories, toggleRefresh } = useSalonData();
  const order = useOrderManagement();
  const payment = usePaymentProcessing();
  const { filterProducts, handleCategorySelect, resetCategoryFilter, selectedCategoryId } = useCategoryFilter(products, activeTab, selectedStaff);
  const { resetOrderFlow, validateOrder } = useOrderFlow(selectedStaff, payment.resetPaymentState, order.clearOrder);
  const [print, setPrint] = useState<PrintData>();

  const now = new Date();

  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");

  const hours = String(now.getHours()).padStart(2, "0");
  const minutes = String(now.getMinutes()).padStart(2, "0");
  const seconds = String(now.getSeconds()).padStart(2, "0");

  useEffect(() => {
    if (payment.selectedCustomerId) {
      fetchOutstandingBalance(payment.selectedCustomerId).then(setOutstandingBalance);
    }
  }, [payment.selectedCustomerId]);

  /** Handlers */
  const handleConfirmOrder = () => {
    if (!validateOrder(order.selectedProducts)) {
      notifyWarning("Please select at least one product.");
      return;
    }
    payment.setCustomerAdd(true);
  };

  const handleCustomerSelect = (customer: CustomerDetails) => {
    payment.setCustomerName(customer.Name);
    payment.setCustomerPhone(customer.phone_number);
    payment.setSelectedCustomerId(customer._id.$oid);
    payment.setCustomerAdd(false);
    payment.setOpenProductList(true);
  };

  const handleAddCustomer = async () => {
    if (!payment.customerName || !payment.customerPhone) {
      notifyError("Please enter both name and phone number.");
      return;
    }
    try {
      const data = await addCustomer(payment.customerName, payment.customerPhone);
      payment.setSelectedCustomerId(data._id);
      notifySuccess("Customer added successfully");
      setCustomerDialogOpen(false);
      payment.setOpenProductList(true);
    } catch (error: any) {
      const message = error.response?.data?.message;

      notifyError(message);
    }
  };

  const handleCompletePayment = async () => {
    payment.setIsProcessingPayment(true);
    try {
      const totals = {
        subtotal: order.calculateSubtotal(),
        total: order.calculateTotal() + payment.calculateTipsTotal(),
      };
      const received = parseFloat(payment.receivedAmount || "0");
      const isFullPayment = payment.paymentMethod !== "Buy on credit" && received >= totals.total;

      const { data, orderData } = await completeOrder({
        customerId: payment.selectedCustomerId!,
        staff: selectedStaff!,
        products: order.selectedProducts,
        totals,
        customer: { Name: payment.customerName, phone_number: payment.customerPhone },
        payment: { method: payment.paymentMethod, received, isFullPayment },
        paymentHistory: { method: payment.paymentMethod, received, isFullPayment },
        discount: Number(order.discount),
        tips: String(payment.tipsAmount),
        time: payment.time,
        outstandingBalance,
      });
      notifySuccess(`Payment ${orderData.paymentStatus} successfully!`);

      const pTotal = Number(roundAmount(order.selectedProducts.reduce((total, product) => total + parseFloat(product.Amount) * (product.quantity || 1), 0)));

      const due = Number(pTotal - Number(order.discount) - Number(payment.receivedAmount));

      const printData: PrintData = {
        CustomerName: payment.customerName || "",
        CustomerPhon: payment.customerPhone || "",
        OrderId: data.Id ?? "",
        OrderStatus: "Confirm",
        discount: Number(order?.discount),
        Date: `${year}-${month}-${day}` || "",
        Time: `${hours}:${minutes}:${seconds}`,
        Products:
          order?.selectedProducts?.map((p: any) => ({
            productName: p.ProductName || "",
            arabicword: p.arabicword || "",
            amount: Number(p.Amount) || 0,
            quantity: Number(p.quantity) || 0,
            totalPrice: Number(p.Amount * p.quantity) || 0,
          })) || [],
        Paid: Number(payment.receivedAmount) ?? "",
        unpaid: due,
        OSB: outstandingBalance,
        paymentMethod: payment.paymentMethod,
        subtotal: Number(totals.total.toFixed(2)),
        total: Number(totals.subtotal.toFixed(2)),
      };

      setPrint(printData);

      resetData();
    } catch {
      notifyError("Failed to complete payment");
    } finally {
      payment.setIsProcessingPayment(false);
    }
  };

  /**----------------------
    * resetData
  -------------------------*/

  const resetData = () => {
    setActiveTab("Staffs");
    setSelectedStaff(null);
    setCustomerDialogOpen(false);
    order.clearOrder();
    payment.resetPaymentState();
    toggleRefresh();
    setResetCategoryFlag(true);
    resetCategoryFilter();
    resetCategoryFilter();
    resetOrderFlow();
  };

  return {
    activeTab,
    setActiveTab,
    selectedStaff,
    setSelectedStaff,
    isCustomerDialogOpen,
    setCustomerDialogOpen,
    outstandingBalance,
    staffMembers,
    products,
    categories,
    filterProducts,
    handleCategorySelect,
    resetOrderFlow,
    order,
    payment,
    handleConfirmOrder,
    handleCustomerSelect,
    handleAddCustomer,
    handleCompletePayment,
    print,
    selectedCategoryId,
    resetCategoryFlag,
    setResetCategoryFlag,
    resetCategoryFilter,
  };
};
